'use client';

import { useEffect, useState } from 'react';

export type NetworkInformationLike = {
  addEventListener?: (type: 'change', listener: () => void) => void;
  downlink?: number;
  effectiveType?: string;
  removeEventListener?: (type: 'change', listener: () => void) => void;
  saveData?: boolean;
  type?: string;
};

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformationLike;
  mozConnection?: NetworkInformationLike;
  webkitConnection?: NetworkInformationLike;
};

export type ReducedImageDataPreference = {
  known: boolean;
  reduced: boolean;
};

export function shouldReduceImageData(connection: NetworkInformationLike | null): boolean {
  if (!connection) return false;
  if (connection.saveData) return true;
  if (connection.type?.toLowerCase() === 'cellular') return true;

  const effectiveType = connection.effectiveType?.toLowerCase();
  if (effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g') {
    return true;
  }

  return typeof connection.downlink === 'number'
    && Number.isFinite(connection.downlink)
    && connection.downlink > 0
    && connection.downlink <= 1.5;
}

export function useReducedImageData(): ReducedImageDataPreference {
  const [preference, setPreference] = useState<ReducedImageDataPreference>({
    known: false,
    reduced: false,
  });

  useEffect(() => {
    const browserNavigator = navigator as NavigatorWithConnection;
    const connection = browserNavigator.connection
      ?? browserNavigator.mozConnection
      ?? browserNavigator.webkitConnection
      ?? null;
    const reducedDataQuery = window.matchMedia?.('(prefers-reduced-data: reduce)') ?? null;
    const updatePreference = () => {
      setPreference({
        known: true,
        reduced: Boolean(reducedDataQuery?.matches) || shouldReduceImageData(connection),
      });
    };

    updatePreference();
    connection?.addEventListener?.('change', updatePreference);
    reducedDataQuery?.addEventListener?.('change', updatePreference);
    return () => {
      connection?.removeEventListener?.('change', updatePreference);
      reducedDataQuery?.removeEventListener?.('change', updatePreference);
    };
  }, []);

  return preference;
}
